import React, { useEffect, useRef } from 'react';
import dayjs from 'dayjs';
import {
    Box,
    IconButton,
    List,
    ListItem,
    TableBody,
    TableRow,
    Typography,
    styled
} from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import CancelIcon from '@mui/icons-material/Cancel';
import CloseIcon from '@mui/icons-material/Close';

import { sickLeaveFlaggedChecker } from '@libs/dateTime';

import ContentContainer from '@libComponents/ContentContainer';
import TableCellContainer from '@libComponents/table/TableCellContainer';
import TableContainer from '@libComponents/table/TableContainer';
import TableHeaderContainer from '@libComponents/table/TableHeaderContainer';
import DialogWrapper from '@libComponents/Dialog';
import { Button, Pagination } from '@libComponents';
import PaginationContainer from '@libComponents/table/PaginationWrapper';
import { useTheme } from '@mui/material/styles';
import ActionList from 'leaveAndHolidays/components/ActionList';

// Styled components
const StyledBox = styled(Box)(() => ({
    width: '100%',
    overflow: 'auto',
    '& table': {
        tableLayout: 'fixed'
    }
}));

const StyledRejectLabel = styled('span')(
    ({ theme: { spacing, palette, shape } }) => ({
        background: palette.rejected.light,
        color: palette.rejected.main,
        padding: spacing(0.5) + ' ' + spacing(2),
        borderRadius: shape.borderRadius
    })
);

const StyledApproveLabel = styled('span')(
    ({ theme: { spacing, palette, shape } }) => ({
        background: palette.approved.light,
        color: palette.approved.main,
        padding: spacing(0.5) + ' ' + spacing(2),
        borderRadius: shape.borderRadius
    })
);

const StyledCancelButton = styled('span')(
    ({ theme: { spacing, palette, shape } }) => ({
        background: palette.cancelled.light,
        color: palette.cancelled.main,
        padding: spacing(0.5) + ' ' + spacing(2),
        borderRadius: shape.borderRadius
    })
);

const StyledPendingButton = styled('span')(
    ({ theme: { spacing, palette, shape } }) => ({
        background: palette.pending.light,
        color: palette.pending.main,
        padding: spacing(0.5) + ' ' + spacing(2),
        borderRadius: shape.borderRadius
    })
);

const StyledMenuBUTTON = styled(Button)(() => ({
    cursor: 'pointer',
    justifyContent: 'flex-start',
    width: 'auto'
}));

const StyledListItem = styled(ListItem)(({ theme: { spacing, palette } }) => ({
    borderBottom: '1px solid',
    borderColor: palette.divider
}));

/**
 * Shared AppliedLeaves component for both employee and admin views
 *
 * @param {Object} props
 * @param {string} props.title - Table title
 * @param {Object} props.tableData - Column configuration
 * @param {Object} props.detailsData - Configuration for details dialog
 * @param {Function} props.fetchDataset - Data fetching function
 * @param {Array} props.data - Table data
 * @param {number} props.totalRows - Total rows for pagination
 * @param {number} props.year - Selected year
 * @param {boolean} props.showCancelAction - Whether to show cancel action
 * @param {Object} props.paginationConfig - Pagination configuration
 * @param {Function} props.LeaveDetailsComponent - Component for leave details
 * @param {Function} props.CancelLeaveComponent - Component for cancel leave
 */

const EMPTY_OBJECT = {};

const AppliedLeavesShared = ({
    title,
    tableData,
    detailsData,
    fetchDataset,
    data,
    totalRows = 0,
    year,
    showCancelAction = false,
    paginationConfig = {},
    LeaveDetailsComponent,
    CancelLeaveComponent,
    getYearDateRange,
    additionalFetchParams = EMPTY_OBJECT,
    usePaginationVariant = false,
    ...props
}) => {
    const [open, setOpen] = React.useState(false);
    const [openReject, setOpenReject] = React.useState(false);
    const [payload, setSelectedPayload] = React.useState({});
    const [rejectPayload, setRejectPayload] = React.useState(null);
    const [currentPage, setCurrentPage] = React.useState(1);
    const [pageSize, setPageSize] = React.useState(
        paginationConfig.rowsPerPage || 10
    );
    const previousYear = useRef(year);
    const isInitialMount = useRef(true);
    const muiTheme = useTheme();

    // Default year date range function
    const defaultGetYearDateRange = (selectedYear) => {
        const startDate = dayjs(`${selectedYear}-01-01`)
            .startOf('year')
            .toISOString();
        const endDate = dayjs(`${selectedYear}-12-31`)
            .endOf('year')
            .toISOString();
        return { startDate, endDate };
    };

    const yearDateRangeFunc = getYearDateRange || defaultGetYearDateRange;

    useEffect(() => {
        // Only fetch data on initial mount or when year actually changes
        if (isInitialMount.current || previousYear.current !== year) {
            const { startDate, endDate } = yearDateRangeFunc(year);
            setCurrentPage(1);

            fetchDataset({
                offset: '',
                from: startDate,
                to: endDate,
                ...additionalFetchParams
            });

            previousYear.current = year;
            isInitialMount.current = false;
        }
    }, [year]); // Only depend on year, which is a primitive value

    const handleChangePage = (event, newPage) => {
        const { startDate, endDate } = yearDateRangeFunc(year);
        setCurrentPage(newPage);

        const fetchParams = {
            offset: (newPage - 1) * pageSize,
            limit: pageSize,
            from: startDate,
            to: endDate,
            ...additionalFetchParams
        };

        fetchDataset(fetchParams);
    };

    const handleRowsPerPageChange = (newPageSize) => {
        const { startDate, endDate } = yearDateRangeFunc(year);
        setPageSize(newPageSize);
        setCurrentPage(1);

        fetchDataset({
            offset: 0,
            limit: newPageSize,
            from: startDate,
            to: endDate,
            ...additionalFetchParams
        });
    };

    const viewLeaveDetails = (row) => {
        setOpen(true);
        setSelectedPayload(row);
    };

    const handleClose = () => {
        setOpen(false);
    };

    // Refetch data for current page (used after approve/reject actions)
    const refetchData = () => {
        const { startDate, endDate } = yearDateRangeFunc(year);
        const fetchParams = {
            offset: (currentPage - 1) * pageSize,
            limit: pageSize,
            from: startDate,
            to: endDate,
            ...additionalFetchParams
        };
        fetchDataset(fetchParams);
    };

    const handleRejectOpen = (row) => {
        setRejectPayload(row);
        setOpenReject(true);
    };

    const handleRejectClose = () => {
        setOpenReject(false);
        setRejectPayload(null);
    };

    const renderStatusLabel = (status) => {
        const statusLower = status.toLowerCase();
        const statusUpper = status.toUpperCase();

        switch (statusLower) {
            case 'approved':
                return <StyledApproveLabel>{statusUpper}</StyledApproveLabel>;
            case 'rejected':
                return <StyledRejectLabel>{statusUpper}</StyledRejectLabel>;
            case 'cancelled':
                return <StyledCancelButton>{statusUpper}</StyledCancelButton>;
            default:
                return <StyledPendingButton>{statusUpper}</StyledPendingButton>;
        }
    };

    const renderTableCell = (row, key, index) => {
        switch (key) {
            case 'startDate':
            case 'endDate':
                return (
                    <TableCellContainer
                        key={key}
                        sx={{
                            color: sickLeaveFlaggedChecker(
                                dayjs(row[key]).format('ddd'),
                                row
                            )
                        }}
                    >
                        {dayjs(row[key]).format('YYYY-MM-DD (ddd)')}
                    </TableCellContainer>
                );
            case 'status':
                return (
                    <TableCellContainer
                        key={key}
                    >
                        {renderStatusLabel(row[key])}
                    </TableCellContainer>
                );
            default:
                return (
                    <TableCellContainer
                        key={key}
                    >
                        {row[key]}
                    </TableCellContainer>
                );
        }
    };

    const renderDetailsDialog = () => {
        if (LeaveDetailsComponent) {
            return (
                <LeaveDetailsComponent
                    payload={payload}
                    handleClose={handleClose}
                />
            );
        }

        // Default details rendering
        return (
            <>
                <Typography variant='h6' sx={{ mb: 2 }}>
                    Leave Details
                </Typography>
                <IconButton
                    aria-label='close'
                    onClick={handleClose}
                    sx={{
                        position: 'absolute',
                        right: 8,
                        top: 8,
                        color: (theme) => theme.palette.text.secondary
                    }}
                >
                    <CloseIcon
                        sx={{
                            color: (theme) => theme.palette.primary.main
                        }}
                    />
                </IconButton>
                <Box>
                    <List dense={false} sx={{ mb: 3 }}>
                        {Object.entries(detailsData || {}).map(
                            ([key, value], index) => {
                                const { type, label } = value;

                                switch (type) {
                                    case 'object':
                                        return (
                                            <StyledListItem key={index}>
                                                <Box sx={{ flex: 1 }}>
                                                    {label}
                                                </Box>
                                                <Box sx={{ flex: 2 }}>
                                                    {payload[key] &&
                                                        payload[key][value.key]}
                                                </Box>
                                            </StyledListItem>
                                        );
                                    case 'array':
                                        return (
                                            <StyledListItem key={index}>
                                                <Box sx={{ flex: 1 }}>
                                                    {label}
                                                </Box>
                                                <Box sx={{ flex: 2 }}>
                                                    {payload[key] &&
                                                        payload[key]
                                                            .map((item) =>
                                                                `${item?.firstname || ''} ${item?.middlename || ''} ${item?.lastname || ''}`.trim()
                                                            )
                                                            .join(', ')}
                                                </Box>
                                            </StyledListItem>
                                        );
                                    case 'date':
                                        return (
                                            <StyledListItem key={index}>
                                                <Box sx={{ flex: 1 }}>
                                                    {label}
                                                </Box>
                                                <Box sx={{ flex: 2 }}>
                                                    {payload[key]
                                                        ? dayjs(
                                                              payload[key]
                                                          ).format('YYYY-MM-DD')
                                                        : ''}
                                                </Box>
                                            </StyledListItem>
                                        );
                                    case 'boolean':
                                        return (
                                            <StyledListItem key={index}>
                                                <Box sx={{ flex: 1 }}>
                                                    {label}
                                                </Box>
                                                <Box sx={{ flex: 2 }}>
                                                    {payload[key]
                                                        ? 'Yes'
                                                        : 'No'}
                                                </Box>
                                            </StyledListItem>
                                        );
                                    case 'file':
                                        return null;
                                    default:
                                        return (
                                            <StyledListItem key={index}>
                                                <Box sx={{ flex: 1 }}>
                                                    {label}
                                                </Box>
                                                <Box sx={{ flex: 2 }}>
                                                    {payload[key]}
                                                </Box>
                                            </StyledListItem>
                                        );
                                }
                            }
                        )}
                    </List>
                </Box>
            </>
        );
    };

    if (!data) return null;

    return (
        <ContentContainer
            title={title}
            headerBg={muiTheme.palette.background.mainHeader}
        >
            <StyledBox>
                <TableContainer>
                    <TableHeaderContainer>
                        {Object.values(tableData).map((th, i) => (
                            <TableCellContainer
                                sx={{
                                    fontWeight: 'bold',
                                    minWidth: i === 0 ? 150 : 'auto'
                                }}
                                typography='caption'
                                key={th}
                            >
                                {th}
                            </TableCellContainer>
                        ))}
                        <TableCellContainer
                            align='right'
                            sx={{
                                fontWeight: 'bold',
                                textAlign: 'right',
                                pr: 4
                            }}
                        >
                            Action
                        </TableCellContainer>
                    </TableHeaderContainer>
                    <TableBody>
                        {data.map((row, i) => (
                            <TableRow key={i} sx={{ cursor: 'pointer' }} onClick={() => viewLeaveDetails(row)}>
                                {Object.keys(tableData).map((key, index) =>
                                    renderTableCell(row, key, index)
                                )}
                                <TableCellContainer align='right' onClick={(e) => e.stopPropagation()} sx={{ pr: 4 }}>
                                    <ActionList row={row}>
                                        <StyledMenuBUTTON
                                            variant='outlined'
                                            type='button'
                                            onClick={(e) => 
                                                viewLeaveDetails(row)
                                            }
                                            size='small'
                                            color='primary'
                                            startIcon={
                                                <InfoIcon color='primary' />
                                            }
                                        >
                                            Details
                                        </StyledMenuBUTTON>
                                        {showCancelAction && (
                                            <StyledMenuBUTTON
                                                variant='outlined'
                                                type='button'
                                                onClick={() =>
                                                    handleRejectOpen(row)
                                                }
                                                size='small'
                                                color='primary'
                                                startIcon={
                                                    <CancelIcon color='primary' />
                                                }
                                                disabled={
                                                    row.status !== 'pending'
                                                }
                                            >
                                                Cancel Leave
                                            </StyledMenuBUTTON>
                                        )}
                                    </ActionList>
                                </TableCellContainer>
                            </TableRow>
                        ))}
                    </TableBody>
                </TableContainer>

                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                    }}
                >
                    {usePaginationVariant ? (
                        <Pagination
                            count={Math.ceil(totalRows / pageSize)}
                            onChange={handleChangePage}
                            page={currentPage}
                            sx={{ display: 'flex', justifyContent: 'center' }}
                            isRowsPerPageVarient={true}
                            rowsPerPageProps={{
                                name: paginationConfig.name || 'appliedLeaves',
                                onChange: handleRowsPerPageChange,
                                value: pageSize
                            }}
                        />
                    ) : (
                        <PaginationContainer
                            count={Math.ceil(
                                totalRows / (paginationConfig.rowsPerPage || 10)
                            )}
                            onChange={handleChangePage}
                            page={currentPage}
                            color='primary'
                        />
                    )}
                </Box>
            </StyledBox>

            {/* Details Dialog */}
            {LeaveDetailsComponent && props.selfContainedDialog ? (
                <LeaveDetailsComponent
                    open={open}
                    payload={payload}
                    handleClose={handleClose}
                    onActionSuccess={refetchData}
                />
            ) : (
                <DialogWrapper
                    open={open}
                    onClose={handleClose}
                    sx={{ textAlign: 'center' }}
                >
                    {renderDetailsDialog()}
                </DialogWrapper>
            )}

            {/* Cancel Leave Dialog */}
            {showCancelAction && CancelLeaveComponent && (
                <DialogWrapper
                    open={openReject}
                    onClose={handleRejectClose}
                    sx={{ textAlign: 'center' }}
                >
                    <Box sx={{ minWidth: '600px', minHeight: '200px' }}>
                        <CancelLeaveComponent
                            rejectPayload={rejectPayload}
                            onReject={handleRejectClose}
                        />
                    </Box>
                </DialogWrapper>
            )}
        </ContentContainer>
    );
};

export default AppliedLeavesShared;
